import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";

export default function GlobalSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const navigate = useNavigate();

    const onChange = (v) => {
        setQ(v);
        clearTimeout(timer.current);
        if (v.trim().length < 2) { setResults(null); setOpen(false); return; }
        timer.current = setTimeout(async () => {
            try {
                const res = await api.get(`/search`, { params: { q: v } });
                setResults(res.data);
                setOpen(true);
            } catch (e) { console.error("Global search failed:", e); setResults(null); }
        }, 300);
    };

    const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };

    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    data-testid="global-search-input"
                    value={q}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => results && setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 200)}
                    placeholder="Pesquisar processos, legislação, prazos..."
                    className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-petroleum/20 focus:border-petroleum transition-colors"
                />
            </div>
            {open && results && (
                <div className="absolute mt-2 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-y-auto z-50" data-testid="search-results">
                    {["processes", "legislation", "deadlines"].every((k) => results[k].length === 0) && (
                        <div className="p-4 text-sm text-slate-400">Nenhum resultado encontrado.</div>
                    )}
                    {results.processes?.length > 0 && (
                        <div className="p-2">
                            <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-slate-400">Processos</div>
                            {results.processes.map((p) => (
                                <button key={p.id} onClick={() => go(`/processos/${p.id}`)} data-testid={`search-result-process-${p.id}`} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm">
                                    <span className="font-mono font-semibold text-petroleum">{p.process_number}</span> · {p.substance || "—"} · {p.municipality}/{p.uf}
                                </button>
                            ))}
                        </div>
                    )}
                    {results.legislation?.length > 0 && (
                        <div className="p-2 border-t border-slate-100">
                            <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-slate-400">Legislação</div>
                            {results.legislation.map((l) => (
                                <button key={l.id} onClick={() => go(`/legislacao/${l.id}`)} data-testid={`search-result-legislation-${l.id}`} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm">{l.title}</button>
                            ))}
                        </div>
                    )}
                    {results.deadlines?.length > 0 && (
                        <div className="p-2 border-t border-slate-100">
                            <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-slate-400">Prazos</div>
                            {results.deadlines.map((d) => (
                                <button key={d.id} onClick={() => go(`/prazos`)} data-testid={`search-result-deadline-${d.id}`} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm">{d.obligation_type} — {d.description}</button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
